// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import loadAccountSummary from "@steiner/App/Handlers/XNG/AccountSummary/LoadAccountSummary";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { GridContainerProps } from "@steiner/MobiusOverrides/GridContainer/GridContainer";
import Select from "@steiner/MobiusOverrides/Select/Select";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { months } from "@steiner/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showShipTo = "showShipTo",
    showYear = "showYear",
    showMonth = "showMonth",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showShipTo]: boolean;
        [fields.showYear]: boolean;
        [fields.showMonth]: boolean;
    };
}
export interface AccountSummaryFiltersStyles {
    container?: GridContainerProps;
    shipToGridItem?: GridItemProps;
    yearGridItem?: GridItemProps;
    monthGridItem?: GridItemProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
}

export const accountSummaryFiltersStyles: AccountSummaryFiltersStyles = {};

const styles = accountSummaryFiltersStyles;

const shipTosParameter: GetShipTosApiParameter = {
    page: 1,
    pageSize: 20,
    expand: ["validation", "excludeShowAll", "excludeOneTime"],
};

const mapStateToProps = (state: CustomAppState) => {
    return {
        shipTosDataView: getShipTosDataView(state, shipTosParameter),
        accountSummary: state.xAccountSummary.accountSummary,
    };
};

const mapDispatchToProps = {
    loadAccountSummary,
    loadShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const AccountSummaryFilters: FC<Props> = ({
    loadShipTos,
    loadAccountSummary,
    fields,
    shipTosDataView,
    accountSummary,
}) => {
    const [selectedShipTo, setSelectedShipTo] = useState("-2");
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedYear, setSelectedYear] = useState("");

    useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos(shipTosParameter);
        }
    }, []);

    return (
        <GridContainer {...styles.container} data-test-selector="account-summary-filters">
            {fields.showShipTo && (
                <GridItem {...styles.shipToGridItem}>
                    <Select
                        value={selectedShipTo}
                        label={translate("Ship To Address")}
                        labelPosition="top"
                        onChange={e => {
                            setSelectedShipTo(e.target.value);
                            loadAccountSummary({
                                customerSequence: e.target.value,
                                year: selectedYear,
                                month: selectedMonth,
                            });
                        }}
                        data-test-selector="accountSummary_shipToFilter"
                    >
                        <option disabled value="-2">
                            {translate("Select an option")}
                        </option>
                        <option value="-1">{translate("Show All")}</option>
                        {shipTosDataView.value &&
                            shipTosDataView.value.map(shipTo => (
                                <option key={shipTo.id} value={shipTo.customerSequence}>
                                    {shipTo.label}
                                </option>
                            ))}
                    </Select>
                </GridItem>
            )}
            {fields.showYear && (
                <GridItem {...styles.yearGridItem}>
                    <Select
                        value={selectedMonth}
                        label={translate("Month")}
                        labelPosition="top"
                        onChange={e => {
                            setSelectedMonth(e.target.value);
                            loadAccountSummary({
                                customerSequence: selectedShipTo,
                                year: selectedYear,
                                month: e.target.value,
                            });
                        }}
                        data-test-selector="accountSummary_yearFilter"
                    >
                        <option disabled value="">
                            {translate("Select an option")}
                        </option>
                        {months.map(month => (
                            <option key={month} value={month}>
                                {month}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fields.showMonth && accountSummary && (
                <GridItem {...styles.monthGridItem}>
                    <Select
                        value={selectedYear}
                        label={translate("Year")}
                        labelPosition="top"
                        onChange={e => {
                            setSelectedYear(e.target.value);
                            loadAccountSummary({
                                customerSequence: selectedShipTo,
                                year: e.target.value,
                                month: selectedMonth,
                            });
                        }}
                        data-test-selector="accountSummary_monthFilter"
                    >
                        <option disabled value="">
                            {translate("Select an option")}
                        </option>
                        {accountSummary.years.map(year => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            <GridItem {...styles.buttonsItem}>
                <Button
                    {...styles.clearFiltersButton}
                    onClick={() => {
                        setSelectedShipTo("-1");
                        setSelectedMonth("");
                        setSelectedYear("");
                        loadAccountSummary({ customerSequence: "-1", year: "", month: "" });
                    }}
                    data-test-selector="accountSummary_clearFiltersButton"
                    aria-label="Clear Filters"
                >
                    {translate("Clear Filters")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSummaryFilters),
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["AccountSummaryPage", "InvoiceHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showShipTo,
                displayName: "Ship to Address",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showYear,
                displayName: "Year",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showMonth,
                displayName: "Month",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
